import toast from 'react-hot-toast';

export class ToastHelper {
  /**
   * Show a success toast notification
   * @param message The success message to display
   * @param duration Optional duration in ms (default: 3000)
   */
  static success(message: string, duration?: number) {
    toast.success(message, { duration: duration || 3000 });
  }

  /**
   * Show an error toast notification
   * @param message The error message to display
   * @param duration Optional duration in ms (default: 5000)
   */
  static error(message: string, duration?: number) {
    toast.error(message, { duration: duration || 5000 });
  }

  /**
   * Show an info toast notification
   * @param message The info message to display
   * @param duration Optional duration in ms (default: 4000)
   */
  static info(message: string, duration?: number) {
    toast(message, {
      duration: duration || 4000,
      icon: 'ℹ️',
    });
  }

  /**
   * Show a warning toast notification
   * @param message The warning message to display
   * @param duration Optional duration in ms (default: 4000)
   */
  static warning(message: string, duration?: number) {
    toast(message, {
      duration: duration || 4000,
      icon: '⚠️',
      style: {
        background: '#ffc107',
        color: '#000',
      },
    });
  }

  /**
   * Show a promise toast that updates based on promise resolution
   * Useful for async operations like API calls
   * @param promise The promise to track
   * @param messages Messages to show for loading, success, and error states
   */
  static promise<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    }
  ) {
    return toast.promise(promise, messages);
  }

  /**
   * Dismiss a specific toast by ID or all toasts
   * @param toastId Optional ID of the toast to dismiss
   */
  static dismiss(toastId?: string) {
    toast.dismiss(toastId);
  }

  /**
   * Show a custom toast with full control over options
   * @param message The message to display
   * @param options Toast options
   */
  static custom(message: string, options?: Record<string, unknown>) {
    toast(message, options);
  }
}
